import { useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronDownIcon } from 'lucide-react';
import { Control, Controller, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { MultiSelectField, type MultiSelectOption } from '@/components/atoms/MultiSelect';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { CAPACITY_LIMITS } from '@/shared/types/capacity-policy';
import { Member } from '@/shared/types/member';
import { numberFieldProps } from '@/util/forms';
import { agentOverrideSchema } from '@/util/validation/schemas/dialog-schemas';

import { OverrideDraft } from './types';

type EditorForm = {
  user_ids: string[];
  max_conversations: number;
  fair_distribution_limit: number;
};

type Props = {
  mode: 'create' | 'edit';
  /** Members selectable in create mode (those without an existing override). */
  availableMembers: Member[];
  /** The member being edited (edit mode) — shown locked. */
  editingMember?: Member;
  /** Existing values when editing. */
  editing?: OverrideDraft;
  /** Policy values used as the initial limits when creating. */
  defaults: { max_conversations: number; fair_distribution_limit: number };
  onSave: (drafts: OverrideDraft[]) => void;
  onCancel: () => void;
};

const memberLabel = (member?: Member) => member?.name || member?.email || '';

/** Trigger summary text: placeholder when empty, else "First name" or "First name +N". */
const getSelectionSummary = (
  selected: string[],
  options: MultiSelectOption[],
  placeholder: string
) => {
  if (selected.length === 0) {
    return placeholder;
  }
  const firstLabel = options.find((o) => o.value === selected[0])?.label ?? selected[0];
  return selected.length > 1 ? `${firstLabel} +${selected.length - 1}` : firstLabel;
};

const NumberField = ({
  id,
  label,
  control,
  name,
  min,
  max,
}: {
  id: string;
  label: string;
  control: Control<EditorForm>;
  name: 'max_conversations' | 'fair_distribution_limit';
  min: number;
  max: number;
}) => (
  <div className="flex-1">
    <Label htmlFor={id} className="mb-2 block text-sm leading-5 font-medium">
      {label}
    </Label>
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <>
          <Input
            id={id}
            type="number"
            min={min}
            max={max}
            aria-invalid={!!error}
            {...numberFieldProps(field)}
          />
          {error && <p className="mt-1 text-xs text-destructive">{error.message}</p>}
        </>
      )}
    />
  </div>
);

const AgentOverrideEditor = ({
  mode,
  availableMembers,
  editingMember,
  editing,
  defaults,
  onSave,
  onCancel,
}: Props) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EditorForm>({
    mode: 'onChange',
    resolver: yupResolver(agentOverrideSchema) as Resolver<EditorForm>,
    defaultValues: {
      user_ids: editing ? [editing.user_id] : [],
      max_conversations: editing?.max_conversations ?? defaults.max_conversations,
      fair_distribution_limit: editing?.fair_distribution_limit ?? defaults.fair_distribution_limit,
    },
  });

  const memberOptions: MultiSelectOption[] = useMemo(
    () => availableMembers.map((m) => ({ value: m.user_id, label: m.name, description: m.email })),
    [availableMembers]
  );

  const onValid = handleSubmit((data) => {
    onSave(
      data.user_ids.map((user_id) => ({
        user_id,
        max_conversations: data.max_conversations,
        fair_distribution_limit: data.fair_distribution_limit,
      }))
    );
  });

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base leading-none font-medium text-foreground">
        {t('environments.agent_override')}
      </h3>

      <div className="flex flex-col gap-2">
        <Label className="text-sm leading-5 font-medium text-foreground">
          {t('environments.agent')}
        </Label>
        {mode === 'edit' ? (
          <div className="flex h-10 w-full items-center gap-2 rounded-md border border-border bg-muted/50 px-3 text-sm text-foreground">
            <Avatar size="xxs">
              <AvatarImage src={editingMember?.avatar} alt={memberLabel(editingMember)} />
              <AvatarFallback size="xxs">{memberLabel(editingMember)[0] || '?'}</AvatarFallback>
            </Avatar>
            <span className="truncate">{memberLabel(editingMember)}</span>
          </div>
        ) : (
          <Controller
            control={control}
            name="user_ids"
            render={({ field }) => (
              <MultiSelectField
                options={memberOptions}
                value={field.value}
                onChange={field.onChange}
                error={!!errors.user_ids}
                placeholder={t('common.search_dots')}
                trigger={
                  <button
                    type="button"
                    className={cn(
                      'group flex h-10 w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      errors.user_ids && 'border-warning'
                    )}
                  >
                    <span
                      className={cn(
                        'truncate',
                        field.value.length === 0 && 'text-muted-foreground'
                      )}
                    >
                      {getSelectionSummary(
                        field.value,
                        memberOptions,
                        t('environments.select_agents')
                      )}
                    </span>
                    <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50 transition-transform group-data-[state=open]:rotate-180" />
                  </button>
                }
              />
            )}
          />
        )}
        {errors.user_ids && (
          <p className="text-xs text-warning">{errors.user_ids.message as string}</p>
        )}
      </div>

      <div className="flex gap-4">
        <NumberField
          id="override_max"
          label={t('environments.max_conversations')}
          control={control}
          name="max_conversations"
          min={CAPACITY_LIMITS.maxConversations.min}
          max={CAPACITY_LIMITS.maxConversations.max}
        />
        <NumberField
          id="override_fair"
          label={t('environments.fair_distribution_limit')}
          control={control}
          name="fair_distribution_limit"
          min={CAPACITY_LIMITS.fairDistributionLimit.min}
          max={CAPACITY_LIMITS.fairDistributionLimit.max}
        />
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="ghost-neutral" size="sm" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="button" variant="primary" size="sm" onClick={onValid}>
          {mode === 'create' ? t('common.add') : t('common.save')}
        </Button>
      </div>
    </div>
  );
};

export default AgentOverrideEditor;
